import {useNuxtApp, navigateTo, abortNavigation, callWithNuxt} from 'nuxt/app'
import type {RouteLocationNormalized} from 'vue-router'
import {useUserStore} from '@/store/user'
import {useAuthStore} from '@/store/auth'
import {useQuantityStore} from '@/store/quantity'
import {User} from 'oidc-client-ts'
import {useGtm} from '@gtm-support/vue-gtm'
import {getTokenRegion, getImpersonationToken, isImpersonationTokenValid} from './useKeycloakToken'
import {getKeycloakHomePath, refreshKeycloakSession} from './useKeycloakSession'
import {useKeycloakAuth} from './useKeycloakAuth'
import {useKeycloakUser} from './useKeycloakUser'
import {isKeycloakSession} from '~/composables/useAuthProvider'

interface MiddlewareDeps {
  // eslint-disable-next-line no-unused-vars
  loginWithKeycloak: (returnPath?: string) => Promise<void>
  handleKeycloakCallback: () => Promise<string | null>
  // eslint-disable-next-line no-unused-vars
  handleKeycloakRestoration: (to: any) => Promise<void>
  logoutFromKeycloak: () => Promise<void>
  // eslint-disable-next-line no-unused-vars
  impersonateKeycloakUser: (targetEmail: string) => Promise<void>
  // eslint-disable-next-line no-unused-vars
  applyKeycloakImpersonation: (accessToken: string) => Promise<void>
}

async function handleKeycloakMiddlewareImpl(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  deps: MiddlewareDeps
) {
  if (!process.client) {
    return null
  }

  const app = useNuxtApp()

  // If Keycloak is not the active session provider, return null to let B2C handle it
  if (!isKeycloakSession()) {
    return null
  }

  const userManager = app.$userManager
  if (!userManager) {
    return null
  }

  const authStore = useAuthStore()
  const userStore = useUserStore()

  const user = await userManager.getUser()
  // eslint-disable-next-line eqeqeq
  const isLoggedIn = user != null && !user.expired

  if (to.path === '/auth/logout') {
    const quantityStore = useQuantityStore()
    quantityStore.clearQuantities()
    await deps.logoutFromKeycloak()
    return abortNavigation()
  }

  const isLoginConsistent = isLoggedIn && userStore.loggedIn
  if (to.path === '/auth/login' && !isLoginConsistent) {
    useGtm()?.trackEvent({
      event: 'login_initiated',
      category: 'login',
      action: 'login_initiated'
    })
    const redirectPath = from.path && !from.path.startsWith('/auth') ? from.path : '/eu/en'
    await deps.loginWithKeycloak(redirectPath)
    return abortNavigation()
  } else if (to.path === '/auth/login' && isLoginConsistent) {
    return navigateTo(getKeycloakHomePath())
  }

  if (to.path === '/auth' || to.path === '/auth/silent') {
    const redirectPath = await deps.handleKeycloakCallback()
    if (redirectPath) {
      return callWithNuxt(app, () => navigateTo(redirectPath, {replace: true}))
    }

    return null
  }

  if (authStore.userObject && authStore.isTokenExpired && user?.refresh_token) {
    try {
      await refreshKeycloakSession(user)
    } catch {
      // refresh token expired/revoked: fall through to the existing logout handling
    }
  }

  // Handle restoration on page refresh
  if (
    !authStore.isTokenExpired
    && authStore?.userObject
    && userStore?.userUuid === ''
    && !to.fullPath.startsWith('/auth#state')
  ) {
    const storedUser = new User({
      id_token: (authStore.userObject as User).id_token,
      access_token: (authStore.userObject as User).access_token,
      refresh_token: (authStore.userObject as User).refresh_token,
      token_type: (authStore.userObject as User).token_type,
      scope: (authStore.userObject as User).scope,
      profile: (authStore.userObject as User).profile,
      session_state: (authStore.userObject as User).session_state,
      expires_at: (authStore.userObject as User).expires_at
    })

    await userManager.storeUser(storedUser)

    // A per-tab impersonation session (sessionStorage) takes precedence over the admin restoration:
    // rebuild the runtime user store as the impersonated user, while the admin stays in the oidc
    // userManager / authStore.userObject (used for logout and for re-enriching further impersonations).
    const impersonationToken = getImpersonationToken()
    const hasImpersonationParam = typeof to.query?.targetEmail === 'string' && !!to.query.targetEmail
    if (impersonationToken && isImpersonationTokenValid(impersonationToken)) {
      await deps.applyKeycloakImpersonation(impersonationToken)
    } else if (!hasImpersonationParam && !to.redirectedFrom?.fullPath?.startsWith('/auth#state=')) {
      await deps.handleKeycloakRestoration(to)
    }
  }

  const accessToken = userStore.accessToken

  // Impersonation request (?targetEmail=...): re-enrich the admin's enriched token with the target user
  const targetEmail = typeof to.query?.targetEmail === 'string' ? to.query.targetEmail : null
  if (targetEmail && authStore?.userObject) {
    await deps.impersonateKeycloakUser(targetEmail)

    const impersonatedToken = userStore.accessToken
    const impersonatedRegion = impersonatedToken ? getTokenRegion(impersonatedToken) : null
    const targetPath = impersonatedRegion && !to.path.startsWith('/' + impersonatedRegion.toLowerCase())
      ? app.$globalization.localePath(to.path, app.$i18n.locale.value, impersonatedRegion)
      : to.path
    return callWithNuxt(app, () => navigateTo({path: targetPath, query: {}}, {replace: true}))
  }

  const tokenRegion = accessToken ? getTokenRegion(accessToken) : null

  if (userStore.newCustomer === null && tokenRegion && !to.path.startsWith('/' + tokenRegion.toLowerCase())) {
    const localePath = app.$globalization.localePath(to.path, app.$i18n.locale.value, tokenRegion)
    if (to.path !== localePath) {
      return navigateTo(localePath)
    } else if (!(to.path.startsWith('/eu/') || to.path.startsWith('/na/') || to.path.startsWith('/global/') || to.path.startsWith('/sea/'))) {
      const redirectPath = `${tokenRegion.toLowerCase()}/${app.$i18n.locale.value}${to.path}`
      return navigateTo(redirectPath)
    }
  }

  return null
}

export const useKeycloakMiddleware = () => {
  const {
    loginWithKeycloak,
    handleKeycloakCallback,
    handleKeycloakRestoration,
    logoutFromKeycloak
  } = useKeycloakAuth()
  const {impersonateKeycloakUser, applyKeycloakImpersonation} = useKeycloakUser()

  return {
    handleKeycloakMiddleware: (to: RouteLocationNormalized, from: RouteLocationNormalized) =>
      handleKeycloakMiddlewareImpl(to, from, {loginWithKeycloak, handleKeycloakCallback, handleKeycloakRestoration, logoutFromKeycloak, impersonateKeycloakUser, applyKeycloakImpersonation})
  }
}
